import { FC, ReactNode } from "react";
import Modal from "../../overlay/Modal/Modal";
import { Button } from "../../controls/Button/Button";

/**
 * "You typed something — leave anyway?" Guards a link inside a form modal that navigates to
 * another page and therefore throws the form away.
 *
 * Lived in `features/appointment-upsert` until `features/task-upsert` grew the same Kalender link,
 * which is the promotion its own note asked for. Only the sentence differs between the two, so the
 * noun arrives as `message` rather than as a `variant` this file would have to know about.
 *
 * Shape and button styling copied from `shared/ui/feedback/ConflictDialog` so the two read as the
 * same family — this one appears in the same flow, on the same modal.
 */

interface Props {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** Name what is lost: "Ihre Eingaben in diesem Termin gehen verloren …". */
  message?: ReactNode;
}

export const DiscardChangesDialog: FC<Props> = ({
  open,
  onConfirm,
  onCancel,
  message = "Ihre Eingaben gehen verloren, wenn Sie die Seite jetzt verlassen.",
}) => (
  <Modal
    empty
    show={open}
    setShow={(next) => {
      if (!next) onCancel();
    }}
    // `empty` skips the header, so there is no title for the dialog to be labelled by. Without this
    // a screen reader announces it as just "dialog".
    ariaLabel="Ungespeicherte Änderungen"
    className="flex w-[520px] flex-col gap-4 p-6"
  >
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            stroke="#D97706"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <p className="text-base font-semibold text-gray-900">Ungespeicherte Änderungen</p>
    </div>

    <p className="text-sm text-gray-600">{message}</p>

    {/* `flex-row-reverse`: the safe action comes first in the DOM so Tab reaches it before the
        destructive one, while the layout keeps "Trotzdem fortfahren" on the left where it has
        always been. */}
    <div className="flex flex-row-reverse gap-3">
      <Button
        onClick={onCancel}
        className="flex-1 rounded-[30px] bg-[#716D6B] font-bold text-white h-[46px]"
        type="button"
      >
        Zurück
      </Button>
      <Button
        onClick={onConfirm}
        className="flex-1 rounded-[30px] bg-amber-600 font-bold text-white h-[46px]"
        type="button"
      >
        Trotzdem fortfahren
      </Button>
    </div>
  </Modal>
);
